import _ from 'lodash';
import { logger } from '@/utils/log';
import { PhoneNumberFormat, PhoneNumberUtil } from 'google-libphonenumber';
import { numberRegExp, PBX_REGIONS, vanityLetter2NumberMap } from '@/plans/ZOOM_PHONE/helper/constants';
import trackings from '@/aspects/trackings';
import { BILLING_CYCLE, trackingPlanTypes } from '@/common/constants';
import { translation } from '@/plugins/i18n';
import { isBizPlusContext, isProPlusContext } from '@/plans/ZOOM_MEETING/common/utils';

const phoneUtil = PhoneNumberUtil.getInstance();

/**
 *
 * @param search {String}
 * @return {String}
 */
export const transferSearchToVanityNumber = (search) => {
  return _.chain(search)
    .map((letter) => vanityLetter2NumberMap.get(_.upperCase(letter)) || letter)
    .join('')
    .value();
};

/**
 *
 * @param phoneNumber {String}
 * @param options {import('@/types').<PbxPlanOptionsDto>}
 * @return {string}
 */
const replacePhoneNumber = (phoneNumber = '', options = {}) => {
  const { search = '', searchLetter = '' } = options;
  const phoneNumberLen = phoneNumber.length;

  const endNumberStr = _.chain([...phoneNumber])
    .takeRight(7)
    .join('')
    .value();
  const startNumberStr = phoneNumber.substr(0, phoneNumberLen - 7);

  return `${startNumberStr}${_.replace(endNumberStr, searchLetter, _.upperCase(search))}`;
};

/**
 *
 * @param phoneNumber {String}
 * @param options {import('@/types').<PbxPlanOptionsDto>}
 * @return {string}
 */
const replacePhoneNumberWithLetter = (phoneNumber = '', options = {}) => {
  const res = _.reduce(phoneNumber, (res, letter, index) => {
    // if the current letter is number
    if (numberRegExp.test(letter)) {
      res.number = `${res.number}${letter}`;
    } else {
      res.map.set(index, letter);
    }

    return res;
  }, {
    map: new Map([]),
    number: '',
  });

  const newNumberList = [...replacePhoneNumber(res.number, options)];

  res.map.forEach((value, key) => {
    if (typeof key === 'number') {
      newNumberList.splice(key, 0, value);
    }
  });

  return _.join(newNumberList, '');
};

/**
 * @param phoneNumber
 * * @param options {import('@/types').<PbxPlanOptionsDto>}
 * @return {string|*}
 */
const formatPhoneNumber = (phoneNumber = '', options = {}) => {
  try {
    const { region = PBX_REGIONS.US_AND_CANADA, isVanity = false } = options;

    const numberRaw = phoneUtil.parseAndKeepRawInput(phoneNumber, _.upperCase(region));
    const viewNumberNational = phoneUtil.format(numberRaw, PhoneNumberFormat.NATIONAL);
    return isVanity ? replacePhoneNumberWithLetter(viewNumberNational, options) : viewNumberNational;
  } catch (e) {
    logger.error('formatPhoneNumber fail', e);
    return replacePhoneNumber(phoneNumber, options);
  }
};

/**
 *
 * @param list {import('@/types').<PbxNumberDto>}
 * @param options {import('@/types').<PbxPlanOptionsDto>}
 * @return {unknown[]}
 */
export const mapBackendPhoneNumberList = (list = [], options = {}) => {
  const { search = '', isVanity = false, type } = options;

  const searchLetter = isVanity ? transferSearchToVanityNumber(search) : '';

  return _.map(list, (item) => {
    const viewNumberNational = formatPhoneNumber(item?.phoneNumber, {
      region: item?.countryIso,
      searchLetter,
      ...options,
    });

    return {
      ...item,
      type,
      label: item?.phoneNumber,
      ariaLabel: viewNumberNational,
      displayName: viewNumberNational,
      viewNumberNational,
    };
  });
};

export const formatPortNumber = (number = '') => {
  return number ? _.chain(number)
    .toString()
    .replace(/[^\d]/g, '')
    .value() : '';
};

export const parsePortNumber = (phoneNumber = '') => {
  return phoneNumber ? _.replace(phoneNumber, /(\d{3})(\d{3})(\d{4})/g, '($1) $2-$3') : '';
};

export const findKey = (obj, val) => {
  for (let key in obj) {
    if (obj.hasOwnProperty(key)) {
      if (obj[key].indexOf(val) !== -1) {
        return key;
      }
    }
  }
  return null;
}

export const trackingAddAnotherCallingPlan = function(activatedPlan = '') {
  trackings.buyInteraction('zoom phone calling plan select', `${activatedPlan} - add another calling plan`);
};

export const trackingAddAdditionalNumbers = function(activatedPlan = '') {
  trackings.buyInteraction('zoom phone calling plan select', `${activatedPlan} - add additional numbers`);
};

export const trackingSelectCallingPlan = function(selectedPlan = '') {
  trackings.buyInteraction('zoom phone calling plan select', `${selectedPlan}`);
};

// also used in zoom one bundle
export const getZPCallingFeesText = (plan) => {
  if(plan.billing_cycle === BILLING_CYCLE.PAY_AS_YOU_GO) {
    return {
      title: translation('billing.pay_you_go'),
      unit: ` ${translation('billing.upfront')}`
    };
  } else {
    return {
      title: translation('billing.prepaid'),
      unit: translation(`billing.common.per_billing_cycle.${plan.billing_cycle}`)
    };
  }
};

export const getCurrentTrackingPlanName = () => {
  if (isProPlusContext) {
    return trackingPlanTypes.ZONE_PRO_PLUS;
  }

  if (isBizPlusContext) {
    return trackingPlanTypes.ZOOM_ONE_BUNDLE;
  }

  return trackingPlanTypes.ZOOM_PHONE;
}
